import type { AssessmentSessionRow } from "@/lib/api/assessments";
import { painEraFor, type RegionSignal } from "@/lib/stiffSoreScoring";

/** Six MAPS body regions — keys match `assessment_sessions` columns. */
export const MAPS_REGION_ROWS = [
  { label: "Neck", regionKey: "neck", capKey: "score_neck" as const, painKey: "pain_score_neck" as const },
  { label: "Headache", regionKey: "headache", capKey: "score_headache" as const, painKey: "pain_score_headache" as const },
  { label: "Mid-back", regionKey: "mid_back", capKey: "score_mid_back" as const, painKey: "pain_score_mid_back" as const },
  { label: "Low back", regionKey: "low_back", capKey: "score_low_back" as const, painKey: "pain_score_low_back" as const },
  { label: "Upper extremity", regionKey: "upper_extremity", capKey: "score_upper_extremity" as const, painKey: "pain_score_upper_ext" as const },
  { label: "Lower extremity", regionKey: "lower_extremity", capKey: "score_lower_extremity" as const, painKey: "pain_score_lower_ext" as const },
] as const;

export type MapsRegionRow = (typeof MAPS_REGION_ROWS)[number];

/**
 * Text + short status label for capacity %.
 *
 * v2.1 (#69): the boundaries follow the instrument's capacity bands. "Strong"
 * starts at 92, not 85 — under v2.0.0 a third of the scale read as nothing to
 * see, which is the exact blindness this release removes. 85-91 is Watch.
 */
export function capacityTone(pct: number | null): { color: string; label: string } {
  if (pct == null) return { color: "text-[var(--color-text-muted)]", label: "—" };
  if (pct >= 92) return { color: "text-[var(--color-success)]", label: "Strong" };
  if (pct >= 85) return { color: "text-[#84cc16]", label: "Watch" };
  if (pct >= 70) return { color: "text-[var(--color-text)]", label: "Solid" };
  if (pct >= 50) return { color: "text-[var(--color-risk-orange)]", label: "At risk" };
  return { color: "text-[var(--color-danger)]", label: "Critical" };
}

export function readRegionCapacity(session: AssessmentSessionRow, row: MapsRegionRow): number | null {
  const v = session[row.capKey];
  return v != null && v > 0 ? v : null;
}

export function readRegionPain(session: AssessmentSessionRow, row: MapsRegionRow): number | null {
  const v = session[row.painKey];
  return v != null && v >= 0 ? v : null;
}

/**
 * Column heading for the 0-10 score, honest about which measure it holds.
 *
 * BLOCKER-B on #67: the same column is an AVERAGE on a v2.0.0 row and a WORST
 * on a v2.1 row. A single fixed heading mislabels half the data.
 */
export function regionScoreColumnLabel(session: AssessmentSessionRow): string {
  return painEraFor(session.assessment_version) === "worst_v2_1"
    ? "Stiff & Sore (0–10)"
    : "Pain (0–10, avg)";
}

/**
 * Builds the state-matrix input from what a session row already carries.
 *
 * Deliberately does NOT require the region-signals table. Capacity and worst
 * score are enough to produce the band, the state and the early-signal flag,
 * which is what ranks the report. Days, descriptor and trajectory add detail on
 * top and are read separately where available.
 */
export function regionSignalFromSession(
  session: AssessmentSessionRow,
  row: MapsRegionRow
): RegionSignal | null {
  const cap = readRegionCapacity(session, row);
  if (cap == null) return null;
  // Only v2.1 rows carry a worst score. Treating a v2.0.0 average as a worst
  // would invent early signals that were never reported.
  const isWorstEra = painEraFor(session.assessment_version) === "worst_v2_1";
  const score = readRegionPain(session, row);
  return {
    regionKey: row.regionKey,
    capacityPercent: cap,
    stiffSoreWorst: isWorstEra && score != null ? score : 0,
    stiffSoreDays7: 0,
    descriptors: [],
    trajectory: null,
    screenedOut: false,
  };
}
